/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Divider,
  IconButton,
  makeStyles,
  Typography,
} from '@material-ui/core';
import { Theme as MaterialUITheme } from '@rjsf/material-ui';
import { useTranslation } from 'react-i18next';
import SaveIcon from '@material-ui/icons/Save';
import EditIcon from '@material-ui/icons/Edit';
import { arrayTemplates, fields, fieldTemplate, objectFieldTemplate, widgets } from 'components/submission/templates';
import withTheme from 'components/submission/rjsf/withTheme';
import UserDetailsInfo from './UserDetailsInfo';

const MuiForm = withTheme(MaterialUITheme);

const useStyles = makeStyles(theme => ({
  root: {
    minWidth: 275,
    padding: '1rem',
    marginBottom: '2rem',
    marginTop: '2rem',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.5rem',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  divider: {
    marginBottom: '2.125rem',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: '500',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem',
      fontWeight: '300',
    },
  },
  form: {
    marginTop: '0rem',
    display: 'grid',
    gridGap: '1rem',
    gridTemplateColumns: '1fr 1fr',
  },
  field: {
    marginRight: '.2rem',
    marginBottom: '1rem',
    paddingRight: '1.2rem',
    width: '100%',
  },
}));

export default function UserEditorForm({
  uiSchema,
  jsonSchema,
  user,
  updateUser,
  isValid,
  checkIfFormIsValid,
  editMode,
  onClick,
  className,
  onSave,
  lastName,
  firstName,
  email,
  userName,
  editRoles,
  isEditAllowed,
}) {
  const idPrefix = 'userEditForm';
  const { t } = useTranslation();
  const classes = useStyles();

  const onFormChange = (form) => {
    checkIfFormIsValid(form);
    updateUser(form.formData);
  };

  const transformErrors = errors => errors;
  const onError = () => {
    // not implemented yet
  };

  return (
    <>
      <Card className={classes.root}>
        <CardContent>
          <Box className={classes.cardHeader}>
            <Typography className={classes.title} color="textPrimary" gutterBottom>
              {t('user-management.my.details')}
            </Typography>
            {isEditAllowed && !editRoles && (
              <CardActions>
                {!editMode ? (
                  <IconButton
                    data-testid="editUserProfileIcon"
                    aria-label={t('actions.edit')}
                    name="editProfile"
                    color="primary"
                    onClick={onClick}
                  >
                    <EditIcon />
                  </IconButton>
                ) : (
                  <>
                    <Button
                      data-testid="cancelEditUserProfile"
                      onClick={onClick}
                      variant="contained"
                      color="default"
                      className={classes.button}
                    >
                      {t('button.cancel.label')}
                    </Button>
                    <Button
                      data-testid="editUserProfile"
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={!isValid}
                      className={classes.button}
                      startIcon={<SaveIcon />}
                      onClick={onSave}
                    >
                      {t('save.changes')}
                    </Button>
                  </>
                )}
              </CardActions>
            )}
          </Box>
          <Divider className={classes.divider} />
          {!editMode ? (
            <UserDetailsInfo firstName={firstName} lastName={lastName} email={email} userName={userName} />
          ) : (
            <MuiForm
              translation={t}
              className={className}
              idPrefix={idPrefix}
              id={idPrefix}
              schema={jsonSchema}
              uiSchema={uiSchema}
              ArrayFieldTemplate={arrayTemplates.formEntitiesArrayTemplate}
              ObjectFieldTemplate={objectFieldTemplate}
              FieldTemplate={fieldTemplate}
              fields={fields}
              widgets={widgets}
              formData={user}
              onChange={onFormChange}
              onError={onError}
              showErrorList={false}
              transformErrors={transformErrors}
              disabled={false}
              liveValidate={true}
            >
              <></>
            </MuiForm>
          )}
        </CardContent>
      </Card>
    </>
  );
}
